import React, { useState, useMemo, useEffect } from "react";
import {
  ModalOverlay,
  Modal as DesignSystemModal,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
  Button as DesignSystemButton,
  TextField,
  Label,
  Input
} from "@nikkei/ise-design-system-component";
import Modal from "components/Common/Modal";
import { useBinderAction, BinderRequestParams } from "./hook";
import { usePersonalBinder } from "../../List/Components/PersonalBinderList/hook";
import styles from "./style.scss";
import { useNavigate, useLocation } from "react-router";
import { useToast } from "hooks/useToast";
import { BinderActionType } from "models/BinderItem";
import { assertUnreachable } from "utils/ts/typeAssertions";
import { BinderForm } from "../BinderForm";
import { CloseConfirmModal } from "../CloseConfirmModal";
import { trackAction } from "utils/helper/analytics";
import { useBinderItem } from "features/BinderPage/Item/hook";
import { AdvancedSearch } from "features/AboutSearch/AdvancedSearch";
import OverseasPremiumModal from "features/OverseasPremium/DetailModal";

const modalTexts = {
  delete: {
    title: "コレクションの削除",
    detail:
      "・一度削除したコレクションは復元できません。\n・共有中のコレクションを削除した場合、共有先のユーザーはこれらを参照できなくなります。",
    button: "削除"
  },
  clone: {
    title: "コレクションの複製",
    detail:
      "複製を行うとフィード設定や保存コンテンツを引き継いだコレクションを作成します。\n複製元のコレクションの共有設定は新たなコレクションには引き継がれません。",
    button: "複製"
  },
  share: {
    title: "コレクションの共有",
    button: "共有"
  },
  edit: {
    title: "編集",
    button: "保存"
  },
  new: {
    title: "コレクションを作成",
    button: "新規作成"
  },
  publish: {
    title: "コレクションの公開",
    button: "保存"
  }
};

const getModalType = (type: BinderActionType) => {
  switch (type) {
    case "delete":
    case "delete_return_index":
      return "delete";
    case "clone":
    case "clone_show_new":
      return "clone";
    case "new":
      return "new";
    case "share":
      return "share";
    case "edit":
      return "edit";
    case "publish":
      return "publish";
  }
  assertUnreachable(type);
};

interface Props {
  onClose?: () => void;
  handleSave?: (params: BinderRequestParams) => void;
  isSaveLoading?: boolean;
  isOpen?: boolean;
  isHome?: boolean;
  errors?: string[];
}

export const BinderActionModal: React.FunctionComponent<Props> = ({
  onClose = () => {},
  handleSave = undefined,
  isSaveLoading = false,
  isOpen = null,
  isHome = false,
  errors = []
}) => {
  const {
    binderActionState,
    closeBinderActionModal,
    cloneBinder,
    shareBinder,
    publishBinder,
    createBinder,
    updateBinder,
    cloneCaption,
    setCloneCaption,
    limit
  } = useBinderAction({ isHome });

  const isOpenModal = useMemo(() => {
    if (isOpen !== null) {
      return isOpen;
    } else {
      return binderActionState !== null;
    }
  }, [binderActionState, isOpen]);

  const { fetchBinderList } = useBinderItem(
    binderActionState?.binderInfo?.binderId ?? ""
  );

  const { fetchPersonalBinders, deletePersonalBinder } = usePersonalBinder({
    isHome
  });

  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [isOpenCompanyModal, setIsOpenCompanyModal] = useState(false);
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const [definitionError, setDefinitionError] = useState("");
  const [backgroundError, setBackgroundError] = useState("");
  const [otherError, setOtherError] = useState("");
  const [taskError, setTaskError] = useState("");
  const [technologyError, setTechnologyError] = useState("");
  const [topicsError, setTopicsError] = useState("");
  const [regulationError, setRegulationError] = useState("");
  const [reOpenFlag, setReOpenFlag] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isOpenHelp, setIsOpenHelp] = useState(false);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);

  const setErrors = (messages: string[] | undefined) => {
    if (!messages) return;
    messages.forEach(message => {
      if (message.includes("定義")) {
        setDefinitionError(message);
      } else if (message.includes("背景")) {
        setBackgroundError(message);
      } else if (message.includes("課題")) {
        setTaskError(message);
      } else if (message.includes("技術")) {
        setTechnologyError(message);
      } else if (message.includes("話題")) {
        setTopicsError(message);
      } else if (message.includes("法規制")) {
        setRegulationError(message);
      } else {
        setOtherError(message);
      }
    });
  };

  const resetErrors = () => {
    setDefinitionError("");
    setBackgroundError("");
    setTaskError("");
    setTechnologyError("");
    setTopicsError("");
    setRegulationError("");
    setOtherError("");
  };

  const getSaveAtlasInfo = (binderInfo: BinderRequestParams) => {
    let count = 0;
    let register = "";
    let feedConditions = "";

    // 企業数の統計
    if (binderInfo.corpCode) {
      count = binderInfo.corpCode.split("|").length;
    }

    // 「企業選択」 or 「Excelインポート」を設定
    if (binderInfo.useMode === "select") {
      register = "企業選択";
    } else if (binderInfo.useMode === "import") {
      register = "Excelインポート";
    }

    // フィード条件
    if (
      binderInfo.includeRelatedCorp === "1" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query) &&
      binderInfo.searchType === "0"
    ) {
      feedConditions = "企業またはキーワード";
    } else if (
      binderInfo.includeRelatedCorp === "1" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query) &&
      binderInfo.searchType === "1"
    ) {
      feedConditions = "企業とキーワード";
    } else if (
      binderInfo.includeRelatedCorp === "1" &&
      !binderInfo.andKeyword &&
      !binderInfo.orKeyword &&
      !binderInfo.notKeyword &&
      !binderInfo.query
    ) {
      feedConditions = "企業";
    } else if (
      binderInfo.includeRelatedCorp === "0" &&
      (binderInfo.andKeyword ||
        binderInfo.orKeyword ||
        binderInfo.notKeyword ||
        binderInfo.query)
    ) {
      feedConditions = "キーワード";
    }

    return { count, register, feedConditions };
  };

  const history = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // ボタン押下後のアクション
  const onAction = async (binderInfo: BinderRequestParams | null = null) => {
    if (!binderActionState) return;

    const binderId = binderActionState.binderInfo?.binderId ?? "";

    // 作成・編集時はAPIを待って閉じる
    if (!["new", "edit"].includes(binderActionState.type)) {
      closeBinderActionModal();
      onClose();
    }

    switch (binderActionState.type) {
      // 削除
      case "delete":
      case "delete_return_index":
        const response = await deletePersonalBinder(binderId);
        trackAction("myCollectionDeleteComplete", response.binderId);
        await fetchPersonalBinders(limit);
        if (binderActionState.type === "delete_return_index") {
          history("/binder/personal", { state: { reserveToast: true } });
        }
        toast.setMessages(["コレクションを削除しました"]);
        break;
      // 複製
      case "clone":
      case "clone_show_new":
        const defaultCaption =
          binderActionState?.binderInfo?.caption + "のコピー";
        // コレクション名の複製処理
        const cloneBinderId = await cloneBinder(binderId, defaultCaption);
        // 複製アクション送信
        trackAction("myCollectionCloneComplete", cloneBinderId);
        // コレクション複製後、コレクション情報を取得し直す
        await fetchPersonalBinders(limit);
        if (binderActionState.type === "clone_show_new") {
          history(`/binder/${cloneBinderId}/article?type=personal`);
        } else {
          toast.setMessages(["コレクションを複製しました"]);
        }
        break;
      // 共有
      case "share":
        const sharedFlg = binderInfo?.setShare ?? false;
        await shareBinder(binderId, sharedFlg);
        trackAction("myCollectionShareSave", {
          id: binderId,
          events: sharedFlg
            ? "コレクション共有完了"
            : "コレクション共有解除完了"
        });
        // 画面を再読み込みし、共有マークの表示/非表示を行うため
        if (location.pathname.match(/\/binder\/[0-9]+/)) {
          // コレクション詳細ページの場合
          await fetchBinderList();
        } else {
          await fetchPersonalBinders(limit);
        }
        toast.setMessages([
          sharedFlg
            ? "コレクションを共有しました"
            : "コレクションの共有を解除しました"
        ]);
        break;
      // 公開
      case "publish":
        const publishedFlg = binderInfo?.setPublish ?? false;
        await publishBinder(binderId, publishedFlg);
        trackAction("myCollectionPublishComplete", binderId);
        // 画面を再読み込みし、公開マークの表示/非表示を行うため
        if (location.pathname.match(/\/binder\/[0-9]+/)) {
          // コレクション詳細ページの場合
          await fetchBinderList();
        } else {
          // 画面を再読み込みし、公開マークの表示/非表示を行うため
          await fetchPersonalBinders(limit);
        }
        toast.setMessages([
          publishedFlg
            ? "コレクションを公開しました"
            : "コレクションの公開を解除しました"
        ]);
        break;
      // 新規作成
      case "new":
        if (!binderInfo) break;
        resetErrors();
        const createParams = { ...binderInfo };
        delete createParams.useMode;
        const createResult = await createBinder(createParams);
        if (!createResult.success) {
          setErrors(createResult.messages);
          return;
        }
        closeBinderActionModal();
        onClose();
        trackAction("myCollectionSave", {
          binderId: createResult.binderId,
          type: "コレクション作成完了",
          count: getSaveAtlasInfo(binderInfo).count,
          register: getSaveAtlasInfo(binderInfo).register,
          feedConditions: getSaveAtlasInfo(binderInfo).feedConditions
        });
        await fetchPersonalBinders(limit);
        toast.setMessages(["コレクションを作成しました"]);
        break;
      // 編集
      case "edit":
        if (!binderInfo) break;
        resetErrors();
        const updateParams = { ...binderInfo };
        delete updateParams.useMode;
        const updateResult = await updateBinder(
          updateParams,
          binderId,
          closeBinderActionModal
        );
        if (!updateResult.success) {
          setErrors(updateResult.messages);
          return;
        }
        trackAction("myCollectionSave", {
          binderId: updateResult.binderId,
          type: "コレクション編集完了",
          count: getSaveAtlasInfo(binderInfo).count,
          register: getSaveAtlasInfo(binderInfo).register,
          feedConditions: getSaveAtlasInfo(binderInfo).feedConditions
        });
        toast.setMessages(["コレクションを編集しました"]);
        break;
    }
  };

  const modalType = binderActionState
    ? getModalType(binderActionState.type)
    : "new";

  const checkReOpenFlag = () => {
    if (reOpenFlag) {
      setReOpenFlag(false);
    }
    return reOpenFlag;
  };

  const closeDeleteModal = () => {
    setDeleteConfirmation("");
    closeBinderActionModal();
    onClose();
  };

  const closeCloneModal = () => {
    closeBinderActionModal();
    onClose();
  };

  useEffect(() => {
    if (!isOpenModal || modalType !== "delete") {
      setDeleteConfirmation("");
    }
    if (!isOpenModal || modalType !== "clone") {
      setCloneCaption("");
    }
  }, [isOpenModal, modalType, setCloneCaption]);

  useEffect(() => {
    setErrors(errors);
  }, [errors]);

  return (
    <>
      {modalType !== "delete" && modalType !== "clone" && (
        <Modal
          isOpen={isOpenModal}
          uncontrolled
          isPreventingClearMessage
          modalClassName={styles.modal}
          title={modalTexts[modalType].title}
          qsFlag={modalType === "new" || modalType === "edit"}
          onClose={() => {
            if (
              isOpenModal &&
              binderActionState &&
              (modalType === "new" || modalType === "edit")
            ) {
              if (isOpenCompanyModal || isOpenImportModal) return true;

              setIsOpenConfirmModal(true);
              // 共通モーダルを閉じなかったこととして返す
              return true;
            }
            closeBinderActionModal();
            onClose();
          }}
          checkReOpenFlag={checkReOpenFlag}
          appendix={
            isOpenHelp && (
              <AdvancedSearch
                isInModal={true}
                onClose={() => setIsOpenHelp(false)}
              />
            )
          }
        >
          {modalType === "new" ||
          modalType === "edit" ||
          modalType === "share" ||
          modalType === "publish" ? (
            <BinderForm
              menuType={modalType}
              binderInfo={binderActionState?.binderInfo}
              onAction={onAction}
              handleSave={handleSave}
              isSaveLoading={isSaveLoading}
              isOpenCompanyModal={isOpenCompanyModal}
              isOpenImportModal={isOpenImportModal}
              setOpenCompanyModal={isOpen => setIsOpenCompanyModal(isOpen)}
              setOpenImportModal={isOpen => setIsOpenImportModal(isOpen)}
              error={{
                definition: definitionError,
                background: backgroundError,
                task: taskError,
                technology: technologyError,
                topics: topicsError,
                other: otherError,
                regulation: regulationError
              }}
              setIsOpenHelp={setIsOpenHelp}
              setIsOpenDetailModal={setIsOpenDetailModal}
              setOtherError={setOtherError}
            />
          ) : null}
        </Modal>
      )}

      <ModalOverlay
        isOpen={isOpenModal && modalType === "delete"}
        isDismissable
        onOpenChange={open => {
          if (!open) closeDeleteModal();
        }}
      >
        <DesignSystemModal>
          <Dialog>
            <DialogHeader>
              <DialogTitle>{modalTexts.delete.title}</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogBody>
              <div className={styles.sectionLabel}>削除するコレクション</div>
              <div
                className={styles.collectionName}
                data-testid="BinderPage-Common-BinderActionModal-collectionName"
              >
                {binderActionState?.binderInfo?.caption}
              </div>
              <div className={styles.sectionLabel}>注意点</div>
              <div className={styles.deleteContent}>
                {modalTexts.delete.detail}
              </div>
              <TextField className={styles.deleteInputField}>
                <Label className={styles.deleteLabelConfirm}>
                  本当に削除しますか？
                </Label>
                <p className={styles.deleteConfirmText}>
                  削除する場合は、コレクション名を入力してください。
                </p>
                <Input
                  placeholder="削除するコレクションの名前"
                  value={deleteConfirmation}
                  onChange={event => setDeleteConfirmation(event.target.value)}
                />
              </TextField>
            </DialogBody>
            <DialogFooter>
              <DesignSystemButton variant="regular" onPress={closeDeleteModal}>
                閉じる
              </DesignSystemButton>
              <DesignSystemButton
                variant="alert"
                isDisabled={
                  deleteConfirmation === "" ||
                  deleteConfirmation !== binderActionState?.binderInfo?.caption
                }
                onPress={() => {
                  onAction();
                }}
                data-testid="BinderPage-Common-BinderActionModal-actionModal"
              >
                削除
              </DesignSystemButton>
            </DialogFooter>
          </Dialog>
        </DesignSystemModal>
      </ModalOverlay>

      <ModalOverlay
        isOpen={isOpenModal && modalType === "clone"}
        isDismissable
        onOpenChange={open => {
          if (!open) closeCloneModal();
        }}
      >
        <DesignSystemModal>
          <Dialog>
            <DialogHeader>
              <DialogTitle>{modalTexts.clone.title}</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogBody>
              <TextField>
                <Label className={styles.sectionLabel}>コレクション名</Label>
                <Input
                  placeholder={
                    binderActionState?.binderInfo?.caption + "のコピー"
                  }
                  value={cloneCaption}
                  onChange={event => setCloneCaption(event.target.value)}
                />
              </TextField>
              <div className={styles.content}>{modalTexts.clone.detail}</div>
            </DialogBody>
            <DialogFooter>
              <DesignSystemButton
                variant="regular"
                onPress={closeCloneModal}
                data-testid="BinderPage-Common-BinderActionModal-closeButton"
              >
                閉じる
              </DesignSystemButton>
              <DesignSystemButton
                variant="primary"
                onPress={() => {
                  onAction();
                }}
                data-testid="BinderPage-Common-BinderActionModal-actionModal"
              >
                複製
              </DesignSystemButton>
            </DialogFooter>
          </Dialog>
        </DesignSystemModal>
      </ModalOverlay>

      <CloseConfirmModal
        isOpen={isOpenConfirmModal}
        keepButtonText="入力を再開"
        onClickKeepButton={() => {
          setIsOpenConfirmModal(false);
          setReOpenFlag(true);
        }}
        onClickCloseButton={() => {
          setIsOpenConfirmModal(false);
          resetErrors();
          closeBinderActionModal();
          onClose();
        }}
      />
      <OverseasPremiumModal
        isOpen={isOpenDetailModal}
        onClose={() => {
          setIsOpenDetailModal(false);
        }}
      />
    </>
  );
};
